import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, Images, LogOut, MapPin, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { AdminLogin } from "@/pages/admin/AdminLogin";
import { GalleryManager } from "@/pages/admin/GalleryManager";
import { ClinicDetails } from "@/pages/admin/ClinicDetails";
import { AppointmentsManager } from "@/pages/admin/AppointmentsManager";
import { EmailSettings } from "@/pages/admin/EmailSettings";

const TABS = [
  { key: "gallery", label: "Gallery", icon: Images },
  { key: "details", label: "Contact & Address", icon: MapPin },
  { key: "appointments", label: "Appointments", icon: CalendarCheck },
  { key: "email", label: "Email Alerts", icon: Mail },
];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("gallery");

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user || user.role !== "admin") return <AdminLogin />;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-medium text-slate-900">Nidaan Dental Care — Admin</h1>
            <p className="text-xs text-slate-500 mt-0.5">Signed in as {user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="outline" className="rounded-md" data-testid="admin-view-site-btn">
                View website
              </Button>
            </Link>
            <Button variant="outline" className="rounded-md" onClick={logout} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors duration-200 ${
                tab === key
                  ? "border-blue-900 text-blue-900"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {tab === "gallery" && <GalleryManager />}
        {tab === "details" && <ClinicDetails />}
        {tab === "appointments" && <AppointmentsManager />}
        {tab === "email" && <EmailSettings />}
      </div>
    </div>
  );
}
